import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import { usePortal } from "../../../../context/PortalContext";
import { useUserAuth } from "../../../../context/AuthContext";
import { IClientData } from "../../../../models/ClientData";
import { trackBehaviourEvent } from "../../../../helpers/userBehaviourTrackingHelpers";
import {
  toE164PhoneFormatter,
  toUk07PhoneFormatter,
} from "../../../../helpers/AssetHelpers/AssetHelpers";
import "./CalendlyWidget.css";

const CALENDLY_WIDGET_SCRIPT_SRC =
  "https://assets.calendly.com/assets/external/widget.js";
let calendlyScriptLoadPromise: Promise<boolean> | undefined;

// Global dedupe: react-calendly registers one window "message" listener per widget instance.
// One Calendly booking postMessage is therefore received by every mounted instance. The map
// tracks in-flight events and collects onApiUpdate callbacks from all instances — the winning
// instance drains all registered callbacks in finally.
const pendingCallbacks = new Map<
  string,
  {
    onStart: Set<() => void>;
    onComplete: Set<() => void>;
  }
>();

interface IProps {
  url: string;
  prefill?: IPrefill;
  onApiUpdateStart?: () => void;
  onApiUpdate?: () => void;
}

export interface IPrefill {
  name?: string;
  email?: string;
  phone?: string;
  customAnswers?: {
    a1?: string;
  };
}

interface CalendlyInlineWidgetOptions {
  url: string;
  parentElement: HTMLElement;
  prefill?: IPrefill;
  resize?: boolean;
}

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget?: (options: CalendlyInlineWidgetOptions) => void;
    };
  }
}

function loadCalendlyScript() {
  if (window.Calendly?.initInlineWidget) {
    return Promise.resolve(true);
  }

  if (calendlyScriptLoadPromise) {
    return calendlyScriptLoadPromise;
  }

  calendlyScriptLoadPromise = new Promise((resolve) => {
    const existingScript = Array.from(document.scripts).find(
      (script) => script.src === CALENDLY_WIDGET_SCRIPT_SRC
    ) as HTMLScriptElement | undefined;
    const script = existingScript ?? document.createElement("script");
    let settled = false;

    const settle = () => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeout);
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);

      const isCalendlyReady = Boolean(window.Calendly?.initInlineWidget);

      if (!isCalendlyReady) {
        calendlyScriptLoadPromise = undefined;
      }

      resolve(isCalendlyReady);
    };
    const handleLoad = () => settle();
    const handleError = () => settle();
    const timeout = setTimeout(settle, 5000);

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    if (!existingScript) {
      script.src = CALENDLY_WIDGET_SCRIPT_SRC;
      script.async = true;
      script.type = "text/javascript";
      document.body.appendChild(script);
    }
  });

  return calendlyScriptLoadPromise;
}

function getCalendlyEmbedUrl(url: string) {
  try {
    const embedUrl = new URL(url);
    embedUrl.searchParams.set("hide_gdpr_banner", "1");

    return embedUrl.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";

    return `${url}${separator}hide_gdpr_banner=1`;
  }
}

export default function CalendlyWidget(props: IProps) {
  const url = props.url;
  const { prefill, onApiUpdateStart, onApiUpdate } = props;
  const { clientData, anonymousClientData, apiUpdateClientData, apiUpdateLeadData } = usePortal();
  const { user } = useUserAuth();
  const calendlyApiKey = process.env.REACT_APP_CALENDLY_API_KEY;
  const [isLoading, setIsLoading] = useState(true);
  const [useFallbackWidget, setUseFallbackWidget] = useState(false);
  const calendlyWidgetRef = useRef<HTMLDivElement>(null);

  const stopLoading = useCallback(() => setIsLoading(false), []);
  const prefillCustomAnswerA1 = prefill?.customAnswers?.a1;
  const prefillEmail = prefill?.email;
  const prefillName = prefill?.name;
  const prefillPhone = prefill?.phone;
  const formattedPrefill = useMemo(() => {
    if (
      !prefillCustomAnswerA1 &&
      !prefillEmail &&
      !prefillName &&
      !prefillPhone
    ) {
      return undefined;
    }

    const formattedPhone = prefillPhone
      ? toE164PhoneFormatter(prefillPhone, "44")
      : undefined;
    const customAnswers = {
      ...(prefillCustomAnswerA1 ? { a1: prefillCustomAnswerA1 } : {}),
      ...(formattedPhone ? { a1: formattedPhone } : {}),
    };

    const hasCustomAnswers = Object.values(customAnswers).some(Boolean);

    return {
      name: prefillName,
      email: prefillEmail,
      phone: prefillPhone,
      ...(hasCustomAnswers ? { customAnswers } : {}),
    };
  }, [prefillCustomAnswerA1, prefillEmail, prefillName, prefillPhone]);
  const calendlyEmbedUrl = useMemo(() => getCalendlyEmbedUrl(url), [url]);

  useCalendlyEventListener({
    onEventScheduled: handleEventScheduled,
    onProfilePageViewed: stopLoading,
    onEventTypeViewed: stopLoading,
    onPageHeightResize: handlePageHeightResize,
  });

  useEffect(() => {
    setIsLoading(true);
    const timeout = setTimeout(stopLoading, 30000);
    return () => clearTimeout(timeout);
  }, [stopLoading, url]);

  useEffect(() => {
    const widgetElement = calendlyWidgetRef.current;

    if (!url || !widgetElement) {
      return;
    }

    setUseFallbackWidget(false);

    let iframe: HTMLIFrameElement | null = null;
    let mutationObserver: MutationObserver | undefined;
    let isCancelled = false;

    const attachIframeLoadHandler = () => {
      iframe = widgetElement.querySelector("iframe");

      if (!iframe) {
        return false;
      }

      iframe.addEventListener("load", stopLoading);
      return true;
    };

    const initializeWidget = async () => {
      const isCalendlyReady = await loadCalendlyScript();

      if (isCancelled) {
        return;
      }

      if (!isCalendlyReady || !window.Calendly?.initInlineWidget) {
        setUseFallbackWidget(true);
        stopLoading();
        return;
      }

      widgetElement.innerHTML = "";
      widgetElement.style.height = "";

      try {
        window.Calendly.initInlineWidget({
          url: calendlyEmbedUrl,
          parentElement: widgetElement,
          prefill: formattedPrefill,
          resize: true,
        });
      } catch {
        setUseFallbackWidget(true);
        stopLoading();
        return;
      }

      if (attachIframeLoadHandler()) {
        return;
      }

      mutationObserver = new MutationObserver(() => {
        if (attachIframeLoadHandler()) {
          mutationObserver?.disconnect();
        }
      });
      mutationObserver.observe(widgetElement, { childList: true });
    };

    initializeWidget();

    return () => {
      isCancelled = true;
      mutationObserver?.disconnect();
      iframe?.removeEventListener("load", stopLoading);
      widgetElement.innerHTML = "";
    };
  }, [calendlyEmbedUrl, formattedPrefill, stopLoading, url]);

  function handlePageHeightResize(e: MessageEvent) {
    const iframe = calendlyWidgetRef.current?.querySelector("iframe");

    if (!iframe || iframe.contentWindow !== e.source) {
      return;
    }

    const height = e.data?.payload?.height;

    if (typeof height === "string" && /^\d+px$/.test(height)) {
      calendlyWidgetRef.current!.style.height = height;
      stopLoading();
    }
  }

  async function fetchCalendlyDetails(uri: string) {
    const response = await fetch(uri, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${calendlyApiKey}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch Calendly details.");
    }
    const data = await response.json();
    return data;
  }

  async function handleEventScheduled(e: any) {
    if (!url) return;

    const eventUri = e?.data?.payload?.event?.uri;
    if (!eventUri) return;

    if (pendingCallbacks.has(eventUri)) {
      const callbacks = pendingCallbacks.get(eventUri)!;
      if (onApiUpdateStart) {
        callbacks.onStart.add(onApiUpdateStart);
        onApiUpdateStart();
      }
      if (onApiUpdate) callbacks.onComplete.add(onApiUpdate);
      return;
    }

    const callbacks = {
      onStart: new Set<() => void>(),
      onComplete: new Set<() => void>(),
    };
    if (onApiUpdateStart) callbacks.onStart.add(onApiUpdateStart);
    if (onApiUpdate) callbacks.onComplete.add(onApiUpdate);
    pendingCallbacks.set(eventUri, callbacks);
    callbacks.onStart.forEach((cb) => cb());

    // Calendly has booked the slot; only sends for a visit that started on a tracked lander
    // (e.g. the annuitycalculator lander after sign-up). Failures below are reported separately.
    trackBehaviourEvent("Appointment Booked");

    try {
      const inviteeUri = e?.data?.payload?.invitee?.uri;

      if (!inviteeUri) {
        return;
      }

      const inviteeDetails = await fetchCalendlyDetails(inviteeUri);
      const eventURI = e?.data?.payload?.event?.uri;
      const eventDetails = await fetchCalendlyDetails(eventURI);
      const combinedDetails = {
        inviteeDetails,
        eventDetails,
      };

      const combinedEventJSON = JSON.stringify(combinedDetails);
      const bookingClientData = clientData ?? anonymousClientData;
      let updatedClientData = { ...bookingClientData } as IClientData;

      // Extract phone number if available
      let primaryPhone = "";
      if (
        inviteeDetails?.resource?.questions_and_answers &&
        Array.isArray(inviteeDetails.resource.questions_and_answers)
      ) {
        for (const qa of inviteeDetails.resource.questions_and_answers) {
          if (qa?.question?.toLowerCase().includes("phone")) {
            primaryPhone = qa.answer.split(" ").join("");
            break;
          }
        }
      }

      if (primaryPhone) {
        updatedClientData.primaryPhone =
          toUk07PhoneFormatter(primaryPhone) ?? primaryPhone;
      }
      // Check if the event is related to an IFA appointment
      if (url.includes("ifa-appointment")) {
        updatedClientData = {
          ...updatedClientData,
          calendlyEventJSON: combinedEventJSON,
          annuity: {
            ...updatedClientData?.annuity,
            bookIFAAppointment: "true",
            bookIFAAppointmentDate: new Date().toLocaleString("en-GB", {
              timeZone: "Europe/London",
            }),
          },
        };
      }
      // Check if the event is a retirement specialist appointment
      if (url.includes("retirement-specialist-appointment")) {
        updatedClientData = {
          ...updatedClientData,
          calendlyEventJSON: combinedEventJSON,
          annuity: {
            ...updatedClientData?.annuity,
            bookGeneralAppointmentDate: new Date().toLocaleString("en-GB", {
              timeZone: "Europe/London",
            }),
          },
        };
      }
      // Check if the event is an annual review appointment
      if (url.includes("annual-review")) {
        updatedClientData = {
          ...updatedClientData,
          calendlyEventJSON: combinedEventJSON,
          annuity: {
            ...updatedClientData?.annuity,
            bookAnnualReviewDate: new Date().toLocaleString("en-GB", {
              timeZone: "Europe/London",
            }),
          },
        };
      }

      if (JSON.stringify(updatedClientData) !== JSON.stringify(bookingClientData)) {
        let responseCode: number | undefined;
        if (user && apiUpdateClientData) {
          responseCode = await apiUpdateClientData(updatedClientData);
        } else if (anonymousClientData && apiUpdateClientData) {
          responseCode = await apiUpdateClientData(updatedClientData);
        } else if (apiUpdateLeadData) {
          responseCode = await apiUpdateLeadData(updatedClientData);
        }
        if (responseCode !== undefined && responseCode !== 200) {
          trackBehaviourEvent("Lander Error", { stage: "booking" });
        }
      }
    } catch (error) {
      console.error("Error handling Calendly event:", error);
      trackBehaviourEvent("Lander Error", { stage: "booking" });
    } finally {
      pendingCallbacks.get(eventUri)?.onComplete.forEach((cb) => cb());
      pendingCallbacks.delete(eventUri);
    }
  }

  return (
    <div className="calendly-inline-widget-container">
      {url && useFallbackWidget ? (
        <InlineWidget url={calendlyEmbedUrl} prefill={formattedPrefill} />
      ) : url ? (
        <div
          ref={calendlyWidgetRef}
          className="calendly-inline-widget"
          data-resize="true"
        />
      ) : null}
      {isLoading && (
        <div className="calendly-loading-overlay">
          <div className="loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="loading-text">Loading appointment scheduler...</div>
          </div>
        </div>
      )}
    </div>
  );
}
